import React, { useState, useMemo, useEffect } from 'react';

const TableModal = ({ isOpen, onClose, title, data, columns, action, navigate, onSearch }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Debounced search effect - hit API when user stops typing
  useEffect(() => {
    if (!onSearch) return; // Skip if no search callback provided
    
    const timeoutId = setTimeout(() => {
      onSearch(searchTerm);
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timeoutId);
  }, [searchTerm, onSearch]);

  // Use data directly from API (server-side filtering if onSearch provided)
  // Otherwise use client-side filtering
  const displayData = useMemo(() => {
    if (onSearch) {
      // Server-side search - use data as is
      return data || [];
    }
    
    // Client-side search fallback
    if (!searchTerm) return data || [];
    
    return (data || []).filter(item => {
      return columns.some(column => {
        const value = item[column.queryKey];
        if (value && typeof value === 'string') {
          return value.toLowerCase().includes(searchTerm.toLowerCase());
        }
        return false;
      });
    });
  }, [data, searchTerm, columns, onSearch]);

  // Calculate pagination
  const totalPages = Math.ceil((displayData?.length || 0) / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = displayData?.slice(startIndex, endIndex) || [];

  // Reset to page 1 when search changes
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  // Reset search when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      setCurrentPage(1);
    }
  }, [isOpen]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className={`fixed inset-0 bg-black bg-opacity-50 transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0'}`} onClick={onClose}></div>

      <div className="flex items-center justify-center min-h-screen p-4">
        <div className={`relative w-full max-w-4xl max-h-full bg-white rounded-lg shadow-lg transform transition-all duration-300 ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`}>
          
          {/* Modal Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h5 className="text-xl font-semibold text-gray-800">
              {title}
            </h5>
            <button
              type="button"
              className="text-gray-400 hover:text-gray-900"
              onClick={onClose}
            >
              <i className='mgc_close_line'></i>
            </button>
          </div>

          {/* Search Bar */}
          <div className="p-4 border-b border-gray-200">
            <div className="relative">
              <input
                type="text"
                placeholder="Cari anggota..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <i className="mgc_search_line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
            </div>
            {displayData && (
              <div className="mt-2 text-sm text-gray-600">
                Menampilkan {currentData.length} dari {displayData.length} data
              </div>
            )}
          </div>

          {/* Modal Body (Isi Tabel) */}
          <div className="p-4 overflow-x-auto">
            <div className="align-middle inline-block min-w-full">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {columns.map((column, index) => (
                      <th key={index} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {column.title}
                      </th>
                    ))}
                    {action && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Action
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentData && currentData.length > 0 ? (
                    currentData.map((item, rowIndex) => (
                      <tr key={rowIndex} className="hover:bg-gray-50">
                        {columns.map((column, colIndex) => (
                          <td key={colIndex} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {/* Render data berdasarkan queryKey atau component */}
                            {column.component ? column.component(item) : item[column.queryKey]}
                          </td>
                        ))}
                        {action && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {action(item, onClose, navigate)}
                          </td>
                        )}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={columns.length + (action ? 1 : 0)} className="px-6 py-4 text-center text-gray-500">
                        {searchTerm ? 'Tidak ada data yang sesuai dengan pencarian.' : 'Tidak ada data.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-4 py-3 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Halaman {currentPage} dari {totalPages}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Sebelumnya
                  </button>
                  
                  {/* Page numbers */}
                  <div className="flex gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNumber;
                      if (totalPages <= 5) {
                        pageNumber = i + 1;
                      } else {
                        if (currentPage <= 3) {
                          pageNumber = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNumber = totalPages - 4 + i;
                        } else {
                          pageNumber = currentPage - 2 + i;
                        }
                      }
                      
                      return (
                        <button
                          key={pageNumber}
                          onClick={() => handlePageChange(pageNumber)}
                          className={`px-3 py-1 border rounded-lg text-sm ${
                            pageNumber === currentPage
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {pageNumber}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Selanjutnya
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Modal Footer */}
          <div className="flex justify-end p-4 border-t border-gray-200">
            <button
              type="button"
              className="btn btn-primary"
              onClick={onClose}
            >
              Tutup
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TableModal;